// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * EventBus
 * 
 * Type-safe event management system for game events
 * Replaces window.dispatchEvent/addEventListener patterns with a centralized bus
 * 
 * Integrated with SoundManager for automatic voice announcements
 * Integrated with SoundEffects for automatic sound effects
 */

import SoundManager from './SoundManager.js';
import GameEventLogger, { GameEventLogger as GameEventLoggerClass } from './GameEventLogger';
import SoundEffects from './SoundEffects';

/**
 * Event Types Registry
 * All game events are defined here for type safety and documentation
 */
export const EVENT_TYPES = {
  // Alert Events
  GAME_ALERT: 'gameAlert',
  
  // Reputation Events
  REPUTATION_CHANGED: 'reputationChanged',
  REPUTATION_RESET: 'reputationReset',
  
  // Navigation Events
  WARP_ENGAGED: 'warpEngaged',
  EXIT_ORBIT: 'exitOrbit',
  ENTER_ORBIT: 'enterOrbit',
  PLANET_ARRIVAL: 'planetArrival',
  SYSTEM_SELECTED: 'systemSelected',
  COURSE_SET: 'courseSet',
  APPROACHING_DESTINATION: 'approachingDestination',
  SYSTEMS_INITIALIZING: 'systemsInitializing',
  
  // Fuel Events
  LOW_FUEL_WARNING: 'lowFuelWarning',
  FUEL_CRITICAL: 'fuelCritical',
  FUEL_DEPLETED: 'fuelDepleted',
  REFUELING_COMPLETE: 'refuelingComplete',
  
  // Warp Event System
  WARP_EVENT_GENERATED: 'warpEventGenerated',
  WARP_EVENT_CLEARED: 'warpEventCleared',
  EVENT_SYSTEM_RESUME: 'eventSystemResume',
  ACTIVE_EVENT_CHANGED: 'activeEventChanged',
  
  // Combat Events
  COMBAT_ENCOUNTER: 'combatEncounter',
  COMBAT_COMPLETE: 'combatComplete',
  COMBAT_ACTIVE: 'combatActive',
  COMBAT_INACTIVE: 'combatInactive',
  COMBAT_INTERRUPT: 'combatInterrupt',
  TARGET_DESTROYED: 'targetDestroyed',
  INCOMING_FIRE: 'incomingFire',
  
  // System Damage Events
  HULL_BREACH: 'hullBreach',
  SHIELDS_CRITICAL: 'shieldsCritical',
  POWER_CRITICAL: 'powerCritical',
  ALL_SYSTEMS_NOMINAL: 'allSystemsNominal',
  REPAIRS_COMPLETE: 'repairsComplete',
  
  // Docking Events
  DOCKING_INITIATED: 'dockingInitiated',
  DOCKING_COMPLETE: 'dockingComplete',
  
  // Scanning Events
  SCANNING: 'scanning',
  SCAN_COMPLETE: 'scanComplete',
  ANOMALY_DETECTED: 'anomalyDetected',
  
  // Warning Events
  COLLISION_WARNING: 'collisionWarning',
  ASTEROID_FIELD_WARNING: 'asteroidFieldWarning',
  QUANTUM_STORM_WARNING: 'quantumStormWarning',
  
  // Weapon Events
  WEAPON_STATE_UPDATE: 'weaponStateUpdate',
  REQUEST_WEAPON_STATE: 'requestWeaponState',
  SHOP_WEAPON_RELOAD: 'shopWeaponReload',
  
  // UI Events
  SYSTEM_STATUS_CLOSED: 'systemStatusClosed',
  
  // Cheat Events
  CHEAT_EVENT_TRIGGERED: 'cheatEventTriggered',

  // Tow Service Events
  ACCEPT_TOW_SERVICE: 'acceptTowService',

  // Crew Events
  CREW_UPDATED: 'crewUpdated',
  TRAINING_COMPLETED: 'trainingCompleted',

  // Ship Events
  CLOAK_TOGGLED: 'cloakToggled',
  JAMMING_TOGGLED: 'jammingToggled',
  SHIP_UPGRADED: 'shipUpgraded',
  QUICK_TRAVEL: 'quickTravel',

  // Generic Events
  GAME_EVENT: 'gameEvent',
  // Achievement Events
  ACHIEVEMENT_UNLOCKED: 'achievementUnlocked',

  // Mission Events
  MISSION_COMPLETE: 'missionComplete',
  MISSION_AVAILABLE: 'missionAvailable',

  // Combat Extended Events
  COMBAT_START: 'combatStart',
  COMBAT_VICTORY: 'combatVictory',

  // Crew Relationship Events
  CREW_RELATIONSHIP_CHANGE: 'crewRelationshipChange',
  CREW_RELATIONSHIP_MILESTONE: 'crewRelationshipMilestone',

  // Economy Events
  CREDITS_EARNED: 'creditsEarned',
  PAYDAY: 'payday',

  // Progression Events
  SYSTEM_VISITED: 'systemVisited',
  FIRST_ORBIT: 'firstOrbit',
  RANK_PROGRESS: 'rankProgress',
  LOW_FUEL: 'lowFuel',
  ALIEN_ENCOUNTER: 'alienEncounter',

  // UI Navigation Events
  OPEN_TAB: 'openTab',
};

/**
 * EventBus Class
 * Centralized event management with type safety and debugging
 */
class EventBus {
  constructor() {
    this.listeners = new Map();
    this.debugMode = false;
    this.eventHistory = [];
    this.maxHistorySize = 100;
  }

  /**
   * Enable debug mode to log all events
   */
  enableDebug() {
    this.debugMode = true;
    console.log('[EventBus] Debug mode enabled');
  }

  /**
   * Disable debug mode
   */
  disableDebug() {
    this.debugMode = false;
    console.log('[EventBus] Debug mode disabled');
  }

  /**
   * Subscribe to an event
   * @param {string} eventType - Event type from EVENT_TYPES
   * @param {function} callback - Callback function
   * @param {object} options - Options (once: boolean)
   * @returns {function} Unsubscribe function
   */
  on(eventType, callback, options = {}) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }

    const listener = {
      callback,
      once: options.once || false,
      id: `${eventType}-${Date.now()}-${Math.random()}`
    };

    this.listeners.get(eventType).push(listener);

    if (this.debugMode) {
      console.log(`[EventBus] Subscribed to ${eventType}`, listener.id);
    }

    // Return unsubscribe function
    return () => this.off(eventType, listener.id);
  }

  /**
   * Subscribe to an event once (auto-unsubscribe after first call)
   * @param {string} eventType - Event type from EVENT_TYPES
   * @param {function} callback - Callback function
   * @returns {function} Unsubscribe function
   */
  once(eventType, callback) {
    return this.on(eventType, callback, { once: true });
  }

  /**
   * Unsubscribe from an event
   * @param {string} eventType - Event type
   * @param {string} listenerId - Listener ID
   */
  off(eventType, listenerId) {
    if (!this.listeners.has(eventType)) return;

    const listeners = this.listeners.get(eventType);
    const index = listeners.findIndex(l => l.id === listenerId);
    
    if (index !== -1) {
      listeners.splice(index, 1);
      
      if (this.debugMode) {
        console.log(`[EventBus] Unsubscribed from ${eventType}`, listenerId);
      }
    }

    // Clean up empty listener arrays
    if (listeners.length === 0) {
      this.listeners.delete(eventType);
    }
  }

  /**
   * Emit an event
   * @param {string} eventType - Event type from EVENT_TYPES
   * @param {any} data - Event data
   */
  emit(eventType, data = null) {
    // Add to history
    this.eventHistory.push({
      type: eventType,
      data,
      timestamp: Date.now(),
      timestampFormatted: new Date().toLocaleTimeString()
    });

    // Trim history

    // Log to GameEventLogger
    this.logToGameEventLogger(eventType, data);
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift();
    }

    // Debug log for WARP_ENGAGED specifically
    if (this.debugMode && eventType === EVENT_TYPES.WARP_ENGAGED) {
      console.log('[EventBus] WARP_ENGAGED event emitted - about to play voice');
    }

    if (this.debugMode) {
      console.log(`[EventBus] Emit ${eventType}`, data);
    }

    // Play voice announcement if available
    this.playVoiceAnnouncement(eventType, data);
    
    // Play sound effect if available
    this.playSoundEffect(eventType, data);

    // Forward to window for legacy listeners
    window.dispatchEvent(data ? new CustomEvent(eventType, { detail: data }) : new Event(eventType));

    // Get listeners for this event
    const listeners = this.listeners.get(eventType);
    if (!listeners || listeners.length === 0) {
      if (this.debugMode) {
        console.warn(`[EventBus] No listeners for ${eventType}`);
      }
      return;
    }

    // Call all listeners
    const listenersToRemove = [];
    listeners.forEach(listener => {
      try {
        listener.callback(data);
        
        // Mark for removal if once
        if (listener.once) {
          listenersToRemove.push(listener.id);
        }
      } catch (error) {
        console.error(`[EventBus] Error in listener for ${eventType}:`, error);
      }
    });

    // Remove once listeners
    listenersToRemove.forEach(id => this.off(eventType, id));
  }

  /**
   * Play voice announcement for event (if available)
   * @param {string} eventType - Event type
   * @param {any} data - Event data
   */
  playVoiceAnnouncement(eventType, data) {
    // Map event types to voice announcement keys
    // SoundManager will auto-check if files exist and gracefully skip if not
    const voiceMap = {
      // Navigation Events
      [EVENT_TYPES.WARP_ENGAGED]: 'warp_engaged',
      [EVENT_TYPES.PLANET_ARRIVAL]: 'destination_reached',
      [EVENT_TYPES.EXIT_ORBIT]: 'undocking_complete',
      [EVENT_TYPES.ENTER_ORBIT]: 'entering_orbit',
      [EVENT_TYPES.SYSTEM_SELECTED]: null, // No voice for this
      [EVENT_TYPES.COURSE_SET]: 'course_set',
      [EVENT_TYPES.APPROACHING_DESTINATION]: 'approaching_destination',
      [EVENT_TYPES.SYSTEMS_INITIALIZING]: 'systems_initializing',
      
      // Fuel Events
      [EVENT_TYPES.LOW_FUEL_WARNING]: 'low_fuel_warning',
      [EVENT_TYPES.FUEL_CRITICAL]: 'fuel_critical',
      [EVENT_TYPES.FUEL_DEPLETED]: 'fuel_depleted',
      [EVENT_TYPES.REFUELING_COMPLETE]: 'refueling_complete',
      
      // Combat Events
      [EVENT_TYPES.COMBAT_ENCOUNTER]: 'red_alert',
      [EVENT_TYPES.COMBAT_COMPLETE]: 'all_clear',
      [EVENT_TYPES.COMBAT_ACTIVE]: 'hostile_ships_detected',
      [EVENT_TYPES.COMBAT_INACTIVE]: null, // No voice for this
      [EVENT_TYPES.COMBAT_INTERRUPT]: 'red_alert',
      [EVENT_TYPES.TARGET_DESTROYED]: 'target_destroyed',
      [EVENT_TYPES.INCOMING_FIRE]: 'incoming_fire',
      
      // System Damage Events
      [EVENT_TYPES.HULL_BREACH]: 'hull_breach',
      [EVENT_TYPES.SHIELDS_CRITICAL]: 'shields_critical',
      [EVENT_TYPES.POWER_CRITICAL]: 'power_critical',
      [EVENT_TYPES.ALL_SYSTEMS_NOMINAL]: 'all_systems_nominal',
      [EVENT_TYPES.REPAIRS_COMPLETE]: 'repairs_complete',
      
      // Docking Events
      [EVENT_TYPES.DOCKING_INITIATED]: 'docking_sequence_initiated',
      [EVENT_TYPES.DOCKING_COMPLETE]: 'docking_complete',
      
      // Scanning Events
      [EVENT_TYPES.SCANNING]: 'scanning',
      [EVENT_TYPES.SCAN_COMPLETE]: 'scan_complete',
      [EVENT_TYPES.ANOMALY_DETECTED]: 'anomaly_detected',
      
      // Warning Events
      [EVENT_TYPES.COLLISION_WARNING]: 'collision_imminent',
      [EVENT_TYPES.ASTEROID_FIELD_WARNING]: 'asteroid_field',
      [EVENT_TYPES.QUANTUM_STORM_WARNING]: 'quantum_storm',
      
      // Warp Event System (random events during warp)
      // These are handled dynamically below based on event.type
      [EVENT_TYPES.WARP_EVENT_GENERATED]: 'dynamic', // Special handling
      [EVENT_TYPES.WARP_EVENT_CLEARED]: null,
      [EVENT_TYPES.EVENT_SYSTEM_RESUME]: null,
      [EVENT_TYPES.ACTIVE_EVENT_CHANGED]: null,
      
      // System Status Events
      [EVENT_TYPES.SYSTEM_STATUS_CLOSED]: null,
      
      // Weapon Events
      [EVENT_TYPES.WEAPON_STATE_UPDATE]: null,
      [EVENT_TYPES.REQUEST_WEAPON_STATE]: null,
      [EVENT_TYPES.SHOP_WEAPON_RELOAD]: null,
      
      // Reputation Events
      [EVENT_TYPES.REPUTATION_CHANGED]: null,
      [EVENT_TYPES.REPUTATION_RESET]: null,
      
      // Alert Events
      [EVENT_TYPES.GAME_ALERT]: null, // Could map based on alert severity
      
      // Tow Service Events
      [EVENT_TYPES.ACCEPT_TOW_SERVICE]: null,
      
      // Cheat Events
      [EVENT_TYPES.CHEAT_EVENT_TRIGGERED]: null,

      // Crew Events
      [EVENT_TYPES.CREW_UPDATED]: null,
    };

    let voiceKey = voiceMap[eventType];
    
    // Handle dynamic event types (like random warp events)
    if (voiceKey === 'dynamic' && data && data.event && data.event.type) {
      // Convert event type to voice key format
      // e.g., "QUANTUM_STORM" -> "quantum_storm"
      voiceKey = data.event.type.toLowerCase();
      if (this.debugMode) {
        console.log(`[EventBus] Dynamic event detected: ${data.event.type} -> voice key: ${voiceKey}`);
      }
    }
    
    // Skip if explicitly set to null or undefined
    if (voiceKey === null || voiceKey === undefined) {
      return;
    }
    
    if (voiceKey && voiceKey !== 'dynamic') {
      // Determine priority based on event type
      const priority = this.getEventPriority(eventType);
      if (this.debugMode) {
        console.log(`[EventBus] Playing voice announcement: ${voiceKey} (priority: ${priority}) for event: ${eventType}`);
      }
      // Queue warp_disengaged before destination_reached on arrival
      if (eventType === EVENT_TYPES.PLANET_ARRIVAL) {
        SoundManager.play('warp_disengaged', { priority });
      }
      SoundManager.play(voiceKey, { priority });
    } else if (!voiceKey) {
      // Log unmapped events for debugging
      if (this.debugMode) {
        console.log(`[EventBus] No voice mapping for event: ${eventType}`);
      }
    }
  }

  /**
   * Get priority level for event type
   * @param {string} eventType - Event type
   * @returns {string} Priority level
   */
  getEventPriority(eventType) {
    const highPriority = [
      EVENT_TYPES.COMBAT_ENCOUNTER,
      EVENT_TYPES.COMBAT_INTERRUPT
    ];

    const mediumPriority = [
      EVENT_TYPES.WARP_ENGAGED,
      EVENT_TYPES.PLANET_ARRIVAL,
      EVENT_TYPES.COMBAT_COMPLETE
    ];

    if (highPriority.includes(eventType)) return 'high';
    if (mediumPriority.includes(eventType)) return 'medium';
    return 'low';
  }

  /**
   * Play sound effect for event
   * @param {string} eventType - Event type
   * @param {any} data - Event data
   */
  playSoundEffect(eventType, data) {
    // Handle special cases first
    
    // WARP_ENGAGED: Play warp_engage once, then start looping warp_travel
    if (eventType === EVENT_TYPES.WARP_ENGAGED) {
      if (this.debugMode) {
        console.log('[EventBus] Warp engaged - playing warp_engage + starting warp_travel loop');
      }
      
      // Stop ambient sounds during warp
      SoundEffects.stop('engine_hum');
      SoundEffects.stop('bridge_ambient');      SoundEffects.play('warp_engage');
      
      // Check if this is a gate warp (data.isGateWarp)
      if (data && data.isGateWarp) {
        // Play gate activation sound instead of normal warp
        SoundEffects.play('gate_activate', { volume: 0.8 });
      }
      
      // Start looping warp travel sound after a short delay (let warp_engage play first)
      setTimeout(() => {
        SoundEffects.play('warp_travel', { loop: true, volume: 0.3 });
      }, 500);
      return;
    }
    
    // PLANET_ARRIVAL: Stop warp_travel loop, play warp_disengage
    if (eventType === EVENT_TYPES.PLANET_ARRIVAL) {
      if (this.debugMode) {
        console.log('[EventBus] Planet arrival - stopping warp_travel loop, playing warp_disengage');
      }
      SoundEffects.stop('warp_travel');
      SoundEffects.play('warp_disengage');
      
      // Restart ambient sounds after warp
      SoundEffects.play('engine_hum', { loop: true, volume: 0.15 });
      SoundEffects.play('bridge_ambient', { loop: true, volume: 0.08 });      return;
    }
    
    // COMBAT_ENCOUNTER: Stop warp_travel if playing (combat during warp)
    if (eventType === EVENT_TYPES.COMBAT_ENCOUNTER) {
      if (this.debugMode) {
        console.log('[EventBus] Combat encounter - stopping warp_travel, playing alert_beep');
      }
      SoundEffects.stop('warp_travel');
      SoundEffects.play('alert_beep');
      return;
    }
    
    // Map event types to sound effect keys (point-in-time sounds)
    const soundEffectMap = {
      // Navigation Events
      [EVENT_TYPES.EXIT_ORBIT]: null,
      [EVENT_TYPES.SYSTEM_SELECTED]: null,
      
      // Combat Events
      [EVENT_TYPES.COMBAT_COMPLETE]: 'scan_complete',
      [EVENT_TYPES.COMBAT_ACTIVE]: 'alert_beep',
      [EVENT_TYPES.COMBAT_INACTIVE]: null,
      [EVENT_TYPES.COMBAT_INTERRUPT]: 'alert_beep',
      
      // Warp Event System
      [EVENT_TYPES.WARP_EVENT_GENERATED]: 'modal_open',
      [EVENT_TYPES.WARP_EVENT_CLEARED]: 'modal_close',
      
      // UI Events
      [EVENT_TYPES.SYSTEM_STATUS_CLOSED]: 'modal_close',
      
      // Other events
      [EVENT_TYPES.ACCEPT_TOW_SERVICE]: 'button_click',
    };

    const soundKey = soundEffectMap[eventType];
    
    if (soundKey) {
      if (this.debugMode) {
        console.log(`[EventBus] Playing sound effect: ${soundKey} for event: ${eventType}`);
      }
      SoundEffects.play(soundKey);
    }
  }

  /**
   * Remove all listeners for an event type
   * @param {string} eventType - Event type
   */
  removeAllListeners(eventType) {
    if (eventType) {
      this.listeners.delete(eventType);
      if (this.debugMode) {
        console.log(`[EventBus] Removed all listeners for ${eventType}`);
      }
    } else {
      this.listeners.clear();
      if (this.debugMode) {
        console.log('[EventBus] Removed all listeners');
      }
    }
  }

  /**
   * Get listener count for an event
   * @param {string} eventType - Event type
   * @returns {number} Listener count
   */
  listenerCount(eventType) {
    const listeners = this.listeners.get(eventType);
    return listeners ? listeners.length : 0;
  }

  /**
   * Get all event types with listeners
   * @returns {Array<string>} Event types
   */
  getEventTypes() {
    return Array.from(this.listeners.keys());
  }

  /**
   * Get event history
   * @param {number} limit - Max number of events to return
   * @returns {Array} Event history
   */
  getHistory(limit = 50) {
    return this.eventHistory.slice(-limit);
  }

  /**
   * Clear event history
   */
  clearHistory() {
    this.eventHistory = [];
    if (this.debugMode) {
      console.log('[EventBus] History cleared');
    }
  }

  /**
   * Get statistics
   * @returns {object} Statistics
   */
  getStats() {
    const stats = {
      totalEventTypes: this.listeners.size,
      totalListeners: 0,
      eventTypes: {},
      historySize: this.eventHistory.length
    };

    this.listeners.forEach((listeners, eventType) => {
      stats.totalListeners += listeners.length;
      stats.eventTypes[eventType] = listeners.length;
    });

    return stats;
  }

  /**
   * Log event to GameEventLogger
   */
  logToGameEventLogger(eventType, data) {
    try {
      const { EVENT_TYPES: LOGGER_TYPES } = GameEventLoggerClass;

      const eventMap = {
        'warpEngaged': LOGGER_TYPES.WARP_ENGAGED,
        'planetArrival': LOGGER_TYPES.WARP_ARRIVED,
        'combatEncounter': LOGGER_TYPES.COMBAT_STARTED,
        'combatComplete': LOGGER_TYPES.COMBAT_VICTORY,
        'reputationChanged': LOGGER_TYPES.REPUTATION_CHANGED,
        'exitOrbit': LOGGER_TYPES.ORBIT_EXITED
      };

      const loggerEventType = eventMap[eventType];
      if (loggerEventType) {
        const logData = typeof data === 'string' ? { message: data } : data;
        GameEventLogger.log(loggerEventType, logData);
      }
    } catch (error) {
      // Silently fail if GameEventLogger is not available
    }
  }
}

// Create singleton instance
const eventBus = new EventBus();

// Export singleton and class
export default eventBus;
export { EventBus };

/**
 * Convenience functions for common patterns
 */

/**
 * Emit a game alert
 * @param {string} message - Alert message
 * @param {string} severity - Alert severity
 * @param {object} options - Additional options
 */
export const emitGameAlert = (message, severity = 'info', options = {}) => {
  eventBus.emit(EVENT_TYPES.GAME_ALERT, { message, severity, ...options });
};

/**
 * Emit reputation change
 * @param {string} speciesId - Species ID
 * @param {number} oldScore - Old reputation score
 * @param {number} newScore - New reputation score
 * @param {string} reason - Reason for change
 */
export const emitReputationChanged = (speciesId, oldScore, newScore, reason) => {
  eventBus.emit(EVENT_TYPES.REPUTATION_CHANGED, { speciesId, oldScore, newScore, reason });
};

/**
 * Emit warp engaged
 */
export const emitWarpEngaged = () => {
  eventBus.emit(EVENT_TYPES.WARP_ENGAGED);
};

/**
 * Emit combat encounter
 */
export const emitCombatEncounter = () => {
  eventBus.emit(EVENT_TYPES.COMBAT_ENCOUNTER);
};

/**
 * Emit system selected
 * @param {object} system - Selected system
 * @param {function} getDisplayDistance - Function to get display distance
 */
export const emitSystemSelected = (system, getDisplayDistance) => {
  eventBus.emit(EVENT_TYPES.SYSTEM_SELECTED, { system, getDisplayDistance });
};
